// Connected · OpenInTabButton
//
// Bordered icon button that opens an entity (or arbitrary URL) in a new in-app tab.
// Composes `@ds/primitives/Button` (variant="default", icon-only) wrapped in an
// AntD Tooltip for the hover hint. The "external" icon flows through
// `@ds/display/Icon` so swapping the icon backend is one-file work.
//
// Owns the navigation lifecycle:
//   - `useEntityNavigate()` for type+id targets.
//   - `useNavigate()` for arbitrary URLs.
//
// API:
//   <OpenInTabButton type="company" id={42} />
//   <OpenInTabButton url="/__ds" />
//   <OpenInTabButton type="invoice" id={7} tooltip="Otwórz fakturę" size="default" />
//
// All visual values come from `@ds/tokens/palette`.

import React from "react";
import { Tooltip } from "antd";
import { Button } from "@ds/primitives/Button";
import { Icon } from "@ds/display/Icon";
import { Inline } from "@ds/layout/Inline";
import { useEntityNavigate, useNavigate, type EntityType } from "@ds/navigation/navigation";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export type OpenInTabButtonInput =
  | {
      /** Entity kind — opens via `useEntityNavigate`. */
      type: EntityType;
      /** Entity numeric primary key. */
      id: number;
      url?: never;
      /** Tooltip label. Default `"Otwórz w nowej karcie"`. */
      tooltip?: string;
      /** Button size scale. Default `"sm"`. */
      size?: "sm" | "md";
      /** `data-testid` applied to the underlying button. */
      testId?: string;
    }
  | {
      type?: never;
      id?: never;
      /** Arbitrary URL — opens via the navigation router. */
      url: string;
      tooltip?: string;
      size?: "sm" | "md";
      testId?: string;
    };

// Connected atom — no Outputs (click is internal navigation), no Actions.
export type OpenInTabButtonProps = OpenInTabButtonInput;

// ─── Implementation ──────────────────────────────────────────────────────

export const OpenInTabButton: React.FC<OpenInTabButtonProps> = ({
  type,
  id,
  url,
  tooltip = "Otwórz w nowej karcie",
  size = "sm",
  testId,
}) => {
  const openEntity = useEntityNavigate();
  const navigate   = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (type !== undefined && id !== undefined) {
      openEntity(type, id, { newTab: true });
    } else if (url) {
      navigate(url, { newTab: true });
    }
  };

  return (
    <Tooltip title={tooltip}>
      {/* span wrapper preserves Tooltip ref attachment without a forwardRef shim */}
      <Inline as="span" inline>
        <Button
          variant="default"
          size={size}
          icon={<Icon name="external" size="sm" />}
          ariaLabel={tooltip}
          label=""
          onClick={handleClick}
          testId={testId}
        />
      </Inline>
    </Tooltip>
  );
};
